import { PusherEvent } from '@mondaygpt/constants'
import { apiCall } from 'modules/core/services/api'
import { useEffect, useRef } from 'react'
import { CreativeService } from '../../../openapi'
import { usePusher } from '../../core/contexts'
import { useChatStatusStore } from '../store/chat-status-store'
import { useCreativeProcessStore, type ScoreUpdatePayload } from '../store/creative-process-store'
import { useCreativeStore } from '../store/creative-store'
import { useGenerationPhaseStore, type GenerationPhaseEvent } from '../store/generation-phase-store'
import { reconcileCreativeStatus } from '../store/generation-status-store'
import { useIdeaStore, type LivePlatform } from '../store/idea-store'
import { usePublishStatusStore } from '../store/publish-status-store'
import type { CreativePublishCompleteData } from '../hooks/usePostPusher'

/** Content keys whose edit can move the score (mirrors the server's set). */
// Per-platform body fields: caption/message (IG/FB/LI), text (X), title/description
// (Pinterest pin + YouTube Shorts). A change to any means a SocialKit re-judge is on its
// way, so the score panel should pulse.
const SCORE_RELEVANT_KEYS = [
  'caption',
  'message',
  'text',
  'title',
  'description',
  'hashtags',
  'altText',
  'imageAttachmentId',
]

/**
 * Oversized Pusher payloads arrive as `{_truncated: true, creativeId}` with the
 * message dropped (Pusher's 10KB cap; see server BasePusher.trigger). Recover
 * by refetching the chat from the server and routing the latest message
 * through the same pending path the full payload would have taken — SideChat
 * dedupes by message id, so a message the open chat already has is a no-op.
 * Read-only recovery: nothing is ever echoed back to the server.
 */
// The controller fires the Pusher event BEFORE persisting the finished
// message, so an immediate GET can read pre-persist state. Two delayed
// attempts bracket the write; storing twice is idempotent (dedupe by id).
const TRUNCATED_REFETCH_DELAYS_MS = [2_000, 8_000]

async function refetchTruncatedMessage(creativeId: string): Promise<void> {
  for (const delay of TRUNCATED_REFETCH_DELAYS_MS) {
    await new Promise(resolve => setTimeout(resolve, delay))
    try {
      // apiCall (not a bare CreativeService call) for the single-flight 401
      // token refresh — a long-idle tab is exactly where truncation recovery
      // must still work. Mirrors SideChat's own background chat refetch.
      const chat = await apiCall(
        CreativeService.creativeControllerGetActiveChatV1,
        null,
        creativeId
      )
      const messages = chat?.messages ?? []
      const last = messages[messages.length - 1]
      if (!last) continue
      useChatStatusStore.getState().storePendingMessage(creativeId, {
        id: last.id,
        role: last.role,
        // The generated type declares parts as string[]; at runtime they are
        // the AI-SDK part objects the pending-message consumers expect.
        parts: (last.parts ?? []) as unknown as Record<string, unknown>[],
      })
    } catch (err) {
      console.warn(`Truncated-message refetch failed for creative ${creativeId}:`, err)
    }
  }
}

/**
 * `creative:content:updated` truncation recovery. A carousel's mediaItems patch
 * is always over the Pusher cap, so for a deck the authoritative event ALWAYS
 * arrives as `{_truncated: true, creativeId}` with the fields dropped; applying
 * that as a patch was a silent no-op. Refetch that ONE creative and route its
 * full content through the state-only path. The server persists BEFORE it
 * fires (CreativeService.updateContentFieldsInternal), so the immediate GET
 * sees the write; the single delayed retry covers a concurrent writer landing
 * right behind it and is coalesced per creative, so a burst of truncated
 * events costs one retry. Read-only recovery: nothing is echoed back as a PUT.
 */
const TRUNCATED_CONTENT_RETRY_MS = 2_000
const truncatedContentRetryTimers = new Map<string, ReturnType<typeof setTimeout>>()

async function refetchCreativeContent(creativeId: string): Promise<void> {
  try {
    const { creative: entity } = await apiCall(
      CreativeService.creativeControllerFindOneV1,
      null,
      creativeId
    )
    const content = entity?.data?.content
    if (content && typeof content === 'object') {
      useCreativeStore
        .getState()
        .applyServerContentPatch(creativeId, content as Record<string, unknown>)
    }
  } catch (err) {
    console.warn(`Truncated-content refetch failed for creative ${creativeId}:`, err)
  }
}

function recoverTruncatedContent(creativeId: string): void {
  void refetchCreativeContent(creativeId)
  const pending = truncatedContentRetryTimers.get(creativeId)
  if (pending) clearTimeout(pending)
  truncatedContentRetryTimers.set(
    creativeId,
    setTimeout(() => {
      truncatedContentRetryTimers.delete(creativeId)
      void refetchCreativeContent(creativeId)
    }, TRUNCATED_CONTENT_RETRY_MS)
  )
}

/**
 * Global Pusher handler for creative chat events.
 * Mounted once in AuthAppLayout — listens to ALL creative events
 * regardless of which creative the user is currently viewing.
 */
export function ChatPusherHandler() {
  const { userChannel } = usePusher()

  // Keep store actions in refs to avoid re-binding on every render
  const getActions = () => ({
    setTyping: useChatStatusStore.getState().setTyping,
    markUnread: useChatStatusStore.getState().markUnread,
    storePendingMessage: useChatStatusStore.getState().storePendingMessage,
    updateCreativeContent: useCreativeStore.getState().updateCreativeContent,
    applyServerContentPatch: useCreativeStore.getState().applyServerContentPatch,
    clearGenerating: useCreativeStore.getState().clearGenerating,
    setPublishComplete: usePublishStatusStore.getState().setComplete,
    applyScoreUpdate: useCreativeProcessStore.getState().applyScoreUpdate,
    setRescoring: useCreativeProcessStore.getState().setRescoring,
    applyPhase: useGenerationPhaseStore.getState().applyPhase,
  })

  const storeRef = useRef(getActions())
  useEffect(() => {
    storeRef.current = getActions()
  })

  useEffect(() => {
    if (!userChannel) return

    const onTypingThought = (data: { creativeId?: string; tools?: string[] }) => {
      if (data.creativeId) storeRef.current.setTyping(data.creativeId, true, data.tools)
    }

    const onTypingEnded = (data: { creativeId?: string }) => {
      if (data.creativeId) {
        storeRef.current.setTyping(data.creativeId, false)
        storeRef.current.clearGenerating(data.creativeId)
      }
    }

    const onContentUpdated = (data: {
      creativeId?: string
      _truncated?: boolean
      fields?: Record<string, unknown>
    }) => {
      if (!data.creativeId) return
      if (data._truncated || !data.fields) {
        // Oversized patch (every carousel deck): the server dropped the fields.
        // Refetch the creative instead of applying an empty patch. Which fields
        // moved is unknown here, so no rescoring pulse.
        recoverTruncatedContent(data.creativeId)
        return
      }
      // Patch-only: this is the SERVER's write arriving over Pusher. Echoing
      // it back as a PUT created a write loop that raced in-flight revises
      // and self-pinned freshly derived platform variants ("user edit" with
      // no user), the Phase 1 e2e blocker.
      storeRef.current.applyServerContentPatch(data.creativeId, data.fields)
      // A score-relevant edit means a re-judge is on its way: pulse the panel.
      if (Object.keys(data.fields).some(k => SCORE_RELEVANT_KEYS.includes(k))) {
        storeRef.current.setRescoring(data.creativeId, true)
      }
    }

    const onScoreUpdated = (data: ScoreUpdatePayload & { creativeId?: string }) => {
      if (!data.creativeId) return
      const { creativeId, ...update } = data
      storeRef.current.applyScoreUpdate(creativeId, update)
    }

    const onGenerationPhase = (data: GenerationPhaseEvent) => {
      storeRef.current.applyPhase(data)
    }

    const onMessageCreated = (data: {
      creativeId?: string
      _truncated?: boolean
      message?: { id: string; role: string; parts: Record<string, unknown>[] }
    }) => {
      if (data.creativeId) {
        storeRef.current.markUnread(data.creativeId)
        if (data.message) {
          storeRef.current.storePendingMessage(data.creativeId, {
            id: data.message.id,
            role: data.message.role,
            parts: data.message.parts,
          })
        } else if (data._truncated) {
          void refetchTruncatedMessage(data.creativeId)
        }
      }
    }

    const onGenerationComplete = (data: { creativeId: string }) => {
      // Treat the Pusher event as a notification only — refetch from the DB so
      // the store always reflects the authoritative server state. The payload
      // carried `status`/`imageUrl`/`name`/`error` previously, but we no longer
      // trust it: the DB write happens before the Pusher fire (see
      // CreativeBackgroundGenerationService), so a fresh GET is guaranteed to
      // see at least what the payload would have said.
      reconcileCreativeStatus(data.creativeId)
    }

    // Extends the shared CreativePublishCompleteData with the platform-specific
    // id keys different publish services attach (X: tweetId, YouTube: videoId,
    // TikTok: publishId) plus permalink. Those flow through as metadata into
    // `${platform}Publish` on the creative content blob, so the "is live"
    // checks flip immediately.
    type PublishCompletePayload = CreativePublishCompleteData & {
      tweetId?: string
      videoId?: string
      permalink?: string
    }
    const onPublishComplete = (data: PublishCompletePayload) => {
      storeRef.current.setPublishComplete(data.creativeId, data)
      if (data.success) {
        const { creativeId, platform, success, error, failureReason, retryable, ...idFields } = data
        const metadata: Record<string, unknown> = {
          lastPublishedAt: new Date().toISOString(),
        }
        for (const [key, value] of Object.entries(idFields)) {
          if (value !== undefined) metadata[key] = value
        }
        storeRef.current.updateCreativeContent(
          creativeId,
          { [`${platform}Publish`]: metadata },
          { userEdit: false }
        )
      }

      // Cross-tab safety net: if PostPublishProgressDialog was dismissed
      // before this async event landed, find the idea that owns this
      // creativeId via its platformResults map and patch it directly so the
      // IdeaCard still flips to 'live'. The idea-store key is `LivePlatform`
      // (e.g. 'youtube_shorts'), distinct from the server payload key
      // (e.g. 'youtube') — we look up by creativeId rather than name to
      // avoid the bridge.
      const ideas = useIdeaStore.getState().ideas
      for (const idea of ideas) {
        if (!idea.platformResults) continue
        for (const [platform, r] of Object.entries(idea.platformResults)) {
          if (r?.creativeId !== data.creativeId) continue
          if (r.state === 'success' || r.state === 'failed') continue
          useIdeaStore.getState().setIdeaPlatformResult(idea.projectId, platform as LivePlatform, {
            creativeId: data.creativeId,
            state: data.success ? 'success' : 'failed',
            permalink: data.permalink,
            error: data.error,
            retryable: data.retryable,
          })
          return
        }
      }
    }

    userChannel.bind(PusherEvent.CreativeTypingThought, onTypingThought)
    userChannel.bind(PusherEvent.CreativeTypingEnded, onTypingEnded)
    userChannel.bind(PusherEvent.CreativeContentUpdated, onContentUpdated)
    userChannel.bind(PusherEvent.CreativeMessageCreated, onMessageCreated)
    userChannel.bind(PusherEvent.CreativeGenerationComplete, onGenerationComplete)
    userChannel.bind(PusherEvent.CreativeScoreUpdated, onScoreUpdated)
    userChannel.bind(PusherEvent.CreativeGenerationPhase, onGenerationPhase)
    userChannel.bind(PusherEvent.CreativePublishComplete, onPublishComplete)

    return () => {
      userChannel.unbind(PusherEvent.CreativeTypingThought, onTypingThought)
      userChannel.unbind(PusherEvent.CreativeTypingEnded, onTypingEnded)
      userChannel.unbind(PusherEvent.CreativeContentUpdated, onContentUpdated)
      userChannel.unbind(PusherEvent.CreativeMessageCreated, onMessageCreated)
      userChannel.unbind(PusherEvent.CreativeGenerationComplete, onGenerationComplete)
      userChannel.unbind(PusherEvent.CreativeScoreUpdated, onScoreUpdated)
      userChannel.unbind(PusherEvent.CreativeGenerationPhase, onGenerationPhase)
      userChannel.unbind(PusherEvent.CreativePublishComplete, onPublishComplete)
    }
  }, [userChannel])

  return null
}
